/**
 * D5 — why one-click turned itself off, in the trader's words. Every board
 * and Game Detail disarm names its cause; the strip shows it as
 * "One-click off: <reason>". Short, plain, and about what the trader did or
 * what changed under them, never about the code.
 */
import { useOneClickStore } from '@/stores/oneClickStore';

export const DISARM_REASON = {
  usPrice: 'You opened a Polymarket US price',
  polyPrice: 'You opened a Polymarket price',
  novigPrice: 'You opened a Novig price',
  ticket: 'You opened the order ticket',
  futures: 'You opened a futures market',
  golf: 'You opened a golf match',
  account: 'Account changed',
  org: 'Org changed',
  sport: 'Board changed (sport)',
  marketType: 'Board changed (market type)',
  date: 'Board changed (date)',
  side: 'Board changed (YES/NO)',
  leftDashboard: 'Left the dashboard',
  leftGame: 'Left this game',
  otherGame: 'Opened another game',
  analytics: 'Switched to Analytics',
  accessUnconfirmed: 'One-click access is not confirmed',
} as const;

/** Access was confirmed and a confirmed profile now says it is off. This is
 *  the trader's account changing, not the board: a manual disarm (D4). */
export const ONE_CLICK_REVOKED_REASON =
  'One-click access was turned off for your account. Ask your manager.';
export const LT2_REVOKED_REASON =
  'Live Trading V2 access was turned off for your account. Ask your manager.';

/** The reason a priced or row click that left the one-click hot path gives. */
export function ticketClickReason(venue: 'kalshi' | 'polymarket'): string {
  return venue === 'polymarket' ? DISARM_REASON.polyPrice : DISARM_REASON.ticket;
}

/**
 * One context field the armed mode depends on: when its value differs from
 * the previous render's, one-click turns off with `reason`. Listed in
 * priority order; the first changed field names the cause.
 */
export interface DisarmContextField {
  value: unknown;
  reason: string;
}

export function changedContextReason(
  prev: readonly DisarmContextField[] | null,
  next: readonly DisarmContextField[],
): string | null {
  if (prev == null) return null;
  for (let i = 0; i < next.length; i += 1) {
    if (!Object.is(prev[i]?.value, next[i].value)) return next[i].reason;
  }
  return null;
}

/**
 * Turn one-click off with a reason, but only while it is armed. A disarm on an
 * already-off mode would replace the reason the trader is reading, and would
 * move the strip's disarm mark past a result that landed after the real
 * disarm (D5: "2 NOT cancelled" after a kill-reflex 1-Click off), hiding it.
 */
export function disarmIfArmed(reason: string): void {
  const store = useOneClickStore.getState();
  if (store.armed) store.disarm(reason);
}
